import styled, { css } from 'styled-components'

import { mq, pxToRem, sizes } from '../../theme'
import Link from '../Link'
import Icon from '../Icon'

import { LangSwitchPosition } from './types'

export const Container = styled.div`
  --lang_background: ${({ theme }) => theme.colors.white};
  --lang_color: ${({ theme }) => theme.colors.black};
  --lang_border-color: ${({ theme }) => theme.colors.greyAA};
  position: relative;
  width: max-content;
  color: var(--lang_color);
`

export const Trigger = styled.button<{ $position?: LangSwitchPosition }>`
  ${({ theme }) => theme.textStyles.m375_buttonSmall}
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${pxToRem(2)};
  height: 100%;
  padding-inline: ${pxToRem(16)};
  color: var(--lang_color);
  background: unset;
  &:hover,
  &:focus,
  &[aria-expanded='true'] {
    text-decoration: underline;
    outline: none;
  }
  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    inset: 0 0 0 0;
    background: var(--lang_background);
    transition: transform 0.3s ease-in-out;
    transform: translateY(-100%);
  }
  &:hover::before,
  &:focus::before,
  &[aria-expanded='true']::before {
    transform: translateY(-0%);
  }
  &::after {
    content: '';
    position: absolute;
    inset-block-start: 100%;
    inset-inline: 0;
    z-index: 50;
    height: 1px;
    background: transparent;
  }
  &[aria-expanded='true']::after {
    background: var(--lang_border-color);
  }
  ${({ $position }) =>
    $position === LangSwitchPosition.bottom &&
    css`
      &::before {
        transform: translateY(100%);
      }
      &:hover::before,
      &:focus::before,
      &[aria-expanded='true']::before {
        transform: translateY(0%);
      }
      &::after {
        inset-block-start: unset;
        inset-block-end: 100%;
      }
    `};
  @media ${mq(sizes.desktop)} {
    font-size: ${pxToRem(16)};
    padding-inline: ${pxToRem(32)};
  }
`
export const Chevron = styled(Icon)<{ $position?: LangSwitchPosition }>`
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(16)};
  transition: transform 0.3s ease-in-out;
  transform: $rotate(0deg);
  [aria-expanded='true'] & {
    transform: rotate(180deg) translateY(2px);
  }
`
export const Label = styled.span`
  position: relative;
  z-index: 2;
  font: inherit;
  @media ${mq(sizes.tablet)} {
  }
`
export const Wrapper = styled.div<{ $position?: LangSwitchPosition }>`
  position: absolute;
  inset: 0;
  inset-block-start: 100%;
  overflow: hidden;
  height: fit-content;
  pointer-events: none;
  ${({ $position }) =>
    $position === LangSwitchPosition.bottom &&
    css`
      inset-block-start: unset;
      inset-block-end: 100%;
      &[hidden] {
        transform: translateY(${pxToRem(10)});
      }
    `};
`
export const List = styled.ul<{ $position?: LangSwitchPosition }>`
  list-style: none;
  z-index: 1;
  display: flex;
  flex-flow: column;
  gap: ${pxToRem(8)};
  padding-block: ${pxToRem(16)};
  background: var(--lang_background);
  transition: transform 0.3s ease-in-out, visibility 0s 0s, opacity 0.1s 0s;
  pointer-events: all;
  &[hidden] {
    display: flex;
    visibility: hidden;
    pointer-events: none;
    opacity: 0;
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out, visibility 0s 0.3s,
      opacity 0.3s 0.15s;
  }
  ${({ $position }) =>
    $position === LangSwitchPosition.bottom &&
    css`
      &[hidden] {
        transform: translateY(100%);
      }
    `};
  @media ${mq(sizes.desktop)} {
    padding-block: ${pxToRem(18)};
    gap: ${pxToRem(16)};
  }
`
export const Item = styled.li``
export const _Link = styled(Link)`
  ${({ theme }) => theme.textStyles.m375_buttonSmall}
  display: flex;
  gap: ${pxToRem(2)};
  padding-inline: ${pxToRem(16)};
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &[aria-current] {
    text-decoration: underline;
  }
  @media ${mq(sizes.desktop)} {
    padding-inline: ${pxToRem(32)};
    font-size: ${pxToRem(16)};
  }
`
export const Check = styled(Icon)`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(16)};
`
